import React, { useState, useEffect } from 'react';
import DataGrid, { GridColumn, GridRow } from '../data/DataGrid';
import '../Modal.css';
import personaService from '../../services/personaService';

interface Persona {
  id: number;
  name: string;
  description: string | null;
  system_prompt: string;
  role_focus: string | null;
  communication_style: string | null;
  icon: string;
  is_system_default: boolean;
  is_public: boolean;
  is_active: boolean;
  temperature: number;
  created_at: string;
}

interface Organization {
  id: number;
  name: string;
  subdomain?: string;
  has_persona?: boolean;
  is_default?: boolean;
  visibility?: 'active' | 'inactive' | 'hidden';
}

interface CreatePersonaForm {
  name: string;
  description: string;
  system_prompt: string;
  temperature: number;
  role_focus: string;
  communication_style: string;
  icon: string;
  is_public: boolean;
  is_system_default: boolean;
}

const PersonasPanel: React.FC = () => {
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPersona, setEditingPersona] = useState<Persona | null>(null);
  const [creating, setCreating] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState<Persona | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [showOrgPersonasModal, setShowOrgPersonasModal] = useState<Organization | null>(null);
  const [orgPersonas, setOrgPersonas] = useState<any[]>([]);
  const [loadingOrgPersonas, setLoadingOrgPersonas] = useState(false);
  const [allOrganizations, setAllOrganizations] = useState<Organization[]>([]);
  
  // Visibility management
  const [showVisibilityModal, setShowVisibilityModal] = useState<Persona | null>(null);
  const [visibilityOrgs, setVisibilityOrgs] = useState<Organization[]>([]);
  const [loadingVisibility, setLoadingVisibility] = useState(false);
  const [visibilityFilter, setVisibilityFilter] = useState<'all' | 'active' | 'inactive' | 'hidden'>('all');
  
  // Icon upload states
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [iconPreview, setIconPreview] = useState<string | null>(null);
  const [uploadingIcon, setUploadingIcon] = useState(false);

  const [formData, setFormData] = useState<CreatePersonaForm>({
    name: '',
    description: '',
    system_prompt: '',
    temperature: 0.5,
    role_focus: 'general',
    communication_style: 'executive_brief',
    icon: '',
    is_public: false,
    is_system_default: false
  });

  useEffect(() => {
    fetchPersonas();
    fetchAllOrganizations();
  }, []);

  const fetchPersonas = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/personas`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch personas: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setPersonas(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch personas');
      }
    } catch (err: any) {
      setError(err.message);
      console.error('Error fetching personas:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : 
              type === 'number' ? parseFloat(value) : 
              value
    }));
  };

  const handleIconFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setError('Invalid file type. Please upload JPEG, PNG, GIF, or WebP image.');
        return;
      }
      
      // Validate file size (2MB)
      if (file.size > 2 * 1024 * 1024) {
        setError('File is too large. Maximum size is 2MB.');
        return;
      }
      
      setIconFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setIconPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleIconUpload = async (personaId: number) => {
    if (!iconFile) return;
    
    try {
      setUploadingIcon(true);
      setError(null);
      
      const result = await personaService.uploadPersonaIcon(personaId, iconFile);
      
      // Update the persona in the list with new icon URL
      setPersonas(prevPersonas =>
        prevPersonas.map(p =>
          p.id === personaId ? { ...p, icon: result.iconUrl } : p
        )
      );
      
      // Clear file input
      setIconFile(null);
      setIconPreview(null);
      
      setSuccess('Icon uploaded successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('Error uploading icon:', error);
      setError(error.message || 'Failed to upload icon');
    } finally {
      setUploadingIcon(false);
    }
  };

  const handleCreatePersona = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    setSuccess(null);

    try {
      if (!formData.name || !formData.system_prompt) {
        throw new Error('Name and system prompt are required');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/personas`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `Failed to create persona: ${response.status}`);
      }

      if (data.success) {
        setSuccess(`Persona "${formData.name}" created successfully!`);
        resetForm();
        setShowCreateForm(false);
        fetchPersonas();
      } else {
        throw new Error(data.message || 'Failed to create persona');
      }
    } catch (err: any) {
      setError(err.message);
      console.error('Error creating persona:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleUpdatePersona = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPersona) return;

    setCreating(true);
    setError(null);
    setSuccess(null);

    try {
      // STEP 1: Upload icon if a file was selected
      if (iconFile) {
        console.log('Uploading icon first...');
        setUploadingIcon(true);
        await personaService.uploadPersonaIcon(editingPersona.id, iconFile);
        console.log('Icon uploaded successfully');
      }

      // STEP 2: Update persona with other fields
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/personas/${editingPersona.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update persona');
      }

      setSuccess(`Persona updated successfully${iconFile ? ' (including icon)' : ''}!`);
      
      // Clear icon upload state
      setIconFile(null);
      setIconPreview(null);
      
      resetForm();
      setEditingPersona(null);
      setShowCreateForm(false);
      fetchPersonas();
    } catch (err: any) {
      setError(err.message || 'Failed to update persona');
    } finally {
      setCreating(false);
      setUploadingIcon(false);
    }
  };

  const handleDeletePersona = async (personaId: number) => {
    if (!window.confirm('Are you sure you want to delete this persona?')) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/personas/${personaId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete persona');
      }

      setSuccess('Persona deleted successfully!');
      fetchPersonas();
    } catch (err: any) {
      setError(err.message || 'Failed to delete persona');
    }
  };

  const startEdit = (persona: Persona) => {
    setEditingPersona(persona);
    setFormData({
      name: persona.name,
      description: persona.description || '',
      system_prompt: persona.system_prompt || '',
      temperature: persona.temperature,
      role_focus: persona.role_focus || 'general',
      communication_style: persona.communication_style || 'executive_brief',
      icon: persona.icon || '',
      is_public: persona.is_public,
      is_system_default: persona.is_system_default
    });
    setShowCreateForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      system_prompt: '',
      temperature: 0.5,
      role_focus: 'general',
      communication_style: 'executive_brief',
      icon: '',
      is_public: false,
      is_system_default: false
    });
    setEditingPersona(null);
  };

  const cancelEdit = () => {
    resetForm();
    setShowCreateForm(false);
  };

  const fetchOrganizations = async (personaId: number) => {
    try {
      setLoadingOrgs(true);
      
      // Fetch organizations with persona assignment status
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/personas/${personaId}/organizations`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch organizations');
      }

      const data = await response.json();
      
      if (data.success) {
        setOrganizations(data.data);
      }
    } catch (err: any) {
      console.error('Error fetching organizations:', err);
      setError(err.message);
    } finally {
      setLoadingOrgs(false);
    }
  };

  // Fetch visibility settings for a persona
  const fetchVisibility = async (personaId: number) => {
    try {
      setLoadingVisibility(true);
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/personas/${personaId}/visibility`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch visibility settings');
      }

      const data = await response.json();
      
      if (data.success) {
        setVisibilityOrgs(data.organizations);
      }
    } catch (err: any) {
      console.error('Error fetching visibility:', err);
      setError(err.message);
    } finally {
      setLoadingVisibility(false);
    }
  };

  // Set visibility for a specific org
  const handleSetVisibility = async (personaId: number, orgId: number, visibility: 'active' | 'inactive' | 'hidden') => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/personas/${personaId}/visibility/${orgId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ visibility })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to set visibility');
      }

      // Update local state
      setVisibilityOrgs(prev => 
        prev.map(org => org.id === orgId ? { ...org, visibility } : org)
      );

      setSuccess(`Visibility set to ${visibility} for organization`);
      setTimeout(() => setSuccess(null), 2000);
    } catch (err: any) {
      console.error('Error setting visibility:', err);
      setError(err.message);
    }
  };

  // Set visibility for all orgs at once
  const handleBulkVisibility = async (personaId: number, visibility: 'active' | 'inactive' | 'hidden', excludeOrgId?: number) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/personas/${personaId}/visibility/bulk`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ visibility, excludeOrgId })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to set bulk visibility');
      }

      // Refresh visibility list
      await fetchVisibility(personaId);

      setSuccess(`Visibility set to ${visibility} for all organizations`);
      setTimeout(() => setSuccess(null), 2000);
    } catch (err: any) {
      console.error('Error setting bulk visibility:', err);
      setError(err.message);
    }
  };

  // Open visibility modal
  const openVisibilityModal = (persona: Persona) => {
    setShowVisibilityModal(persona);
    fetchVisibility(persona.id);
  };

  // Close visibility modal
  const closeVisibilityModal = () => {
    setShowVisibilityModal(null);
    setVisibilityOrgs([]);
    setVisibilityFilter('all');
  };

  // Filter visibility orgs
  const filteredVisibilityOrgs = visibilityOrgs.filter(org => {
    if (visibilityFilter === 'all') return true;
    return org.visibility === visibilityFilter;
  });

  const handleAssignPersona = async (organizationId: number, personaId: number, isDefault: boolean = false) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/personas/${personaId}/assign/${organizationId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ is_default: isDefault })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to assign persona');
      }

      setSuccess(`Persona assigned successfully${isDefault ? ' as default' : ''}!`);
      if (showAssignModal) {
        fetchOrganizations(showAssignModal.id);
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleUnassignPersona = async (organizationId: number, personaId: number) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/personas/${personaId}/assign/${organizationId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to unassign persona');
      }

      setSuccess('Persona unassigned successfully!');
      if (showAssignModal) {
        fetchOrganizations(showAssignModal.id);
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const openAssignModal = (persona: Persona) => {
    setShowAssignModal(persona);
    fetchOrganizations(persona.id);
  };

  const closeAssignModal = () => {
    setShowAssignModal(null);
    setOrganizations([]);
  };

  const fetchAllOrganizations = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/super-admin/organizations`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch organizations');
      }

      const data = await response.json();
      
      if (data.success) {
        setAllOrganizations(data.data);
      }
    } catch (err: any) {
      console.error('Error fetching organizations:', err);
      setError(err.message);
    }
  };

  const fetchOrgPersonas = async (organizationId: number) => {
    try {
      setLoadingOrgPersonas(true);
      
      // Fetch all personas with assignment status for this org
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/super-admin/organizations/${organizationId}/personas`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch organization personas');
      }

      const data = await response.json();
      
      if (data.success) {
        setOrgPersonas(data.data);
      }
    } catch (err: any) {
      console.error('Error fetching organization personas:', err);
      setError(err.message);
    } finally {
      setLoadingOrgPersonas(false);
    }
  };

  const openOrgPersonasModal = (org: Organization) => {
    setShowOrgPersonasModal(org);
    fetchOrgPersonas(org.id);
  };

  const closeOrgPersonasModal = () => {
    setShowOrgPersonasModal(null);
    setOrgPersonas([]);
  };

  const handleAssignPersonaToOrg = async (organizationId: number, personaId: number, isDefault: boolean = false) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/personas/${personaId}/assign/${organizationId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ is_default: isDefault })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to assign persona');
      }

      setSuccess(`Persona assigned successfully${isDefault ? ' as default' : ''}!`);
      if (showOrgPersonasModal) {
        fetchOrgPersonas(showOrgPersonasModal.id);
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleUnassignPersonaFromOrg = async (organizationId: number, personaId: number) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/personas/${personaId}/assign/${organizationId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to unassign persona');
      }

      setSuccess('Persona unassigned successfully!');
      if (showOrgPersonasModal) {
        fetchOrgPersonas(showOrgPersonasModal.id);
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <>
      <div className="header-actions" style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            className={`${showCreateForm ? 'secondary-button' : 'primary-button'}`}
            onClick={() => setShowCreateForm(!showCreateForm)}
            disabled={creating}
          >
            {showCreateForm ? 'View Personas' : 'Create Persona'}
          </button>
          <button 
            className="secondary-button"
            onClick={fetchPersonas}
            disabled={loading}
          >
            Refresh
          </button>
        </div>
        
        {/* Organization selector for reverse assignment */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label htmlFor="org-select" style={{ fontSize: '14px', fontWeight: '500' }}>
            Manage Personas by Organization:
          </label>
          <select
            id="org-select"
            key={showOrgPersonasModal ? 'open' : 'closed'}
            onChange={(e) => {
              const orgId = parseInt(e.target.value);
              const org = allOrganizations.find(o => o.id === orgId);
              if (org) openOrgPersonasModal(org);
              e.target.value = ''; // Reset after selection
            }}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.1)',
              background: 'rgba(255,255,255,0.05)',
              color: 'white',
              fontSize: '14px',
              cursor: 'pointer'
            }}
            defaultValue=""
          >
            <option value="" disabled>Select an organization...</option>
            {allOrganizations.map(org => (
              <option key={org.id} value={org.id}>{org.name}</option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="alert-close">×</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="alert-close">×</button>
        </div>
      )}

      {showCreateForm ? (
        <div className="card">
          <div className="card-header">
            <h2>{editingPersona ? 'Edit Persona' : 'Create New Persona'}</h2>
          </div>
          <div className="card-content">
            <form onSubmit={editingPersona ? handleUpdatePersona : handleCreatePersona}>
              <div className="form-group">
                <label htmlFor="name">Persona Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., CFO Financial Analyst"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <input
                  type="text"
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Brief description of this persona"
                />
              </div>

              <div className="form-group">
                <label htmlFor="system_prompt">System Prompt *</label>
                <textarea
                  id="system_prompt"
                  name="system_prompt"
                  value={formData.system_prompt}
                  onChange={handleInputChange}
                  placeholder="You are a..."
                  rows={6}
                  required
                  style={{ width: '100%', padding: '8px', fontFamily: 'monospace', fontSize: '14px' }}
                />
                <small>This defines the AI's personality and approach</small>
              </div>

              <div className="form-group">
                <label htmlFor="icon">Icon</label>
                
                {/* Show current icon if editing */}
                {editingPersona && (
                  <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div>
                      <strong style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#666' }}>Current Icon:</strong>
                      <img 
                        src={`${(process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/api$/, '')}/api/personas/icon/${editingPersona.id}`}
                        alt="Current icon"
                        style={{ 
                          width: '60px', 
                          height: '60px', 
                          objectFit: 'cover', 
                          borderRadius: '50%',
                          border: '2px solid #ddd'
                        }}
                        onError={(e) => {
                          const img = e.target as HTMLImageElement;
                          img.style.display = 'none';
                          const parent = img.parentElement;
                          if (parent && !parent.querySelector('.no-icon-msg')) {
                            const div = document.createElement('div');
                            div.className = 'no-icon-msg';
                            div.style.padding = '10px';
                            div.style.backgroundColor = '#f3f4f6';
                            div.style.border = '1px dashed #d1d5db';
                            div.style.borderRadius = '4px';
                            div.style.fontSize = '13px';
                            div.style.color = '#6b7280';
                            div.textContent = 'No icon uploaded';
                            parent.appendChild(div);
                          }
                        }}
                      />
                    </div>
                  </div>
                )}
                
                {/* Option 1: Use emoji or path (text input) */}
                <input
                  type="text"
                  id="icon"
                  name="icon"
                  value={formData.icon}
                  onChange={handleInputChange}
                  placeholder="e.g., 🤖 or leave blank to upload image below"
                />
                <small>Emoji (🤖, 👨‍💼, 👩‍💼, 🧠) or leave blank to upload custom image</small>
                
                {/* Option 2: Upload image (only when editing existing persona) */}
                {editingPersona && (
                  <div style={{ 
                    marginTop: '15px', 
                    padding: '15px', 
                    border: '1px solid #ddd', 
                    borderRadius: '4px',
                    backgroundColor: '#f9f9f9'
                  }}>
                    <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '10px' }}>
                      Or upload custom icon image:
                    </label>
                    
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      onChange={handleIconFileChange}
                      style={{ marginBottom: '10px', display: 'block' }}
                    />
                    
                    {iconPreview && (
                      <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                        <img 
                          src={iconPreview} 
                          alt="Icon preview" 
                          style={{ 
                            width: '60px', 
                            height: '60px', 
                            objectFit: 'cover', 
                            borderRadius: '50%',
                            border: '2px solid #ddd'
                          }}
                        />
                        <div style={{ 
                          marginTop: '8px', 
                          padding: '8px', 
                          backgroundColor: '#e7f5e7', 
                          border: '1px solid #4CAF50',
                          borderRadius: '4px',
                          color: '#2d5f2d',
                          fontSize: '13px'
                        }}>
                          ✓ Icon ready! Click "Update Persona" below to save.
                        </div>
                      </div>
                    )}
                    
                    <small style={{ display: 'block', marginTop: '10px', color: '#666', fontStyle: 'italic' }}>
                      Max size: 2MB. Formats: JPEG, PNG, GIF, WebP
                      {iconFile && <span style={{ color: '#4CAF50', fontWeight: 'bold' }}> • File selected: {iconFile.name}</span>}
                    </small>
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="role_focus">Role Focus</label>
                  <select
                    id="role_focus"
                    name="role_focus"
                    value={formData.role_focus}
                    onChange={handleInputChange}
                  >
                    <option value="general">General</option>
                    <option value="cfo">CFO / Financial</option>
                    <option value="coo">COO / Operations</option>
                    <option value="sales_director">Sales Director</option>
                    <option value="ceo">CEO / Strategic</option>
                    <option value="analyst">Data Analyst</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="communication_style">Communication Style</label>
                  <select
                    id="communication_style"
                    name="communication_style"
                    value={formData.communication_style}
                    onChange={handleInputChange}
                  >
                    <option value="executive_brief">Executive Brief</option>
                    <option value="analytical">Analytical</option>
                    <option value="conversational">Conversational</option>
                    <option value="technical">Technical</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="temperature">Temperature</label>
                  <input
                    type="number"
                    id="temperature"
                    name="temperature"
                    value={formData.temperature}
                    onChange={handleInputChange}
                    min="0"
                    max="1"
                    step="0.1"
                  />
                  <small>0 = Focused, 1 = Creative</small>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      name="is_public"
                      checked={formData.is_public}
                      onChange={handleInputChange}
                    />
                    Public Persona
                  </label>
                  <small>Available to all organizations</small>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      name="is_system_default"
                      checked={formData.is_system_default}
                      onChange={handleInputChange}
                    />
                    System Default
                  </label>
                  <small>Fallback for organizations without persona</small>
                </div>
              </div>

              <div className="form-actions">
                <button 
                  type="submit" 
                  className="primary-button"
                  disabled={creating}
                >
                  {creating ? (editingPersona ? 'Updating...' : 'Creating...') : (editingPersona ? 'Update Persona' : 'Create Persona')}
                </button>
                <button 
                  type="button" 
                  className="secondary-button"
                  onClick={cancelEdit}
                  disabled={creating}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-header">
            <h2>Personas ({personas.length})</h2>
          </div>
          <div className="card-content">
            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading personas...</p>
              </div>
            ) : personas.length === 0 ? (
              <div className="empty-state">
                <h3>No Personas Yet</h3>
                <p>Create your first persona to customize AI behavior for organizations.</p>
                <button 
                  className="primary-button"
                  onClick={() => setShowCreateForm(true)}
                >
                  Create First Persona
                </button>
              </div>
            ) : (
              <DataGrid
                columns={[
                  {
                    field: 'icon',
                    headerName: 'Icon',
                    width: 80,
                    renderCell: (row: GridRow) => (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img 
                          src={`${(process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/api$/, '')}/api/personas/icon/${row.id}`}
                          alt="Icon"
                          style={{ 
                            width: '40px', 
                            height: '40px', 
                            objectFit: 'cover', 
                            borderRadius: '50%',
                            border: '2px solid #ddd'
                          }}
                          onError={(e) => {
                            // Show placeholder text if no icon
                            const img = e.target as HTMLImageElement;
                            img.style.display = 'none';
                            const parent = img.parentElement;
                            if (parent && !parent.querySelector('.no-icon-text')) {
                              const text = document.createElement('small');
                              text.className = 'no-icon-text';
                              text.style.color = '#999';
                              text.style.fontSize = '11px';
                              text.textContent = 'No icon';
                              parent.appendChild(text);
                            }
                          }}
                        />
                      </div>
                    )
                  },
                  {
                    field: 'name',
                    headerName: 'Persona Name',
                    width: 200,
                    renderCell: (row: GridRow) => (
                      <div>
                        <strong>{row.name}</strong>
                        {row.description && (
                          <>
                            <br />
                            <small style={{ color: '#888' }}>{row.description}</small>
                          </>
                        )}
                      </div>
                    )
                  },
                  {
                    field: 'role_focus',
                    headerName: 'Role',
                    width: 120,
                    renderCell: (row: GridRow) => (
                      <span style={{ 
                        background: '#667eea', 
                        color: 'white', 
                        padding: '2px 8px', 
                        borderRadius: '12px', 
                        fontSize: '12px' 
                      }}>
                        {row.role_focus || 'general'}
                      </span>
                    )
                  },
                  {
                    field: 'temperature',
                    headerName: 'Temp',
                    width: 80,
                    type: 'number'
                  },
                  {
                    field: 'is_public',
                    headerName: 'Public',
                    width: 90,
                    renderCell: (row: GridRow) => (
                      <span>{row.is_public ? '✅' : '❌'}</span>
                    )
                  },
                  {
                    field: 'is_system_default',
                    headerName: 'Default',
                    width: 100,
                    renderCell: (row: GridRow) => (
                      row.is_system_default ? (
                        <span style={{ 
                          background: '#10b981', 
                          color: 'white', 
                          padding: '2px 8px', 
                          borderRadius: '12px', 
                          fontSize: '12px' 
                        }}>
                          ⭐ Default
                        </span>
                      ) : (
                        <span style={{ color: '#666' }}>-</span>
                      )
                    )
                  },
                  {
                    field: 'is_active',
                    headerName: 'Status',
                    width: 90,
                    renderCell: (row: GridRow) => (
                      <span style={{ 
                        background: row.is_active ? '#10b981' : '#ef4444', 
                        color: 'white', 
                        padding: '2px 8px', 
                        borderRadius: '12px', 
                        fontSize: '12px' 
                      }}>
                        {row.is_active ? 'Active' : 'Inactive'}
                      </span>
                    )
                  },
                  {
                    field: 'created_at',
                    headerName: 'Created',
                    width: 120,
                    type: 'date',
                    valueFormatter: (value: string) => new Date(value).toLocaleDateString()
                  },
                  {
                    field: 'actions',
                    headerName: 'Actions',
                    width: 280,
                    renderCell: (row: GridRow) => (
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        <button 
                          className="secondary-button"
                          onClick={() => {
                            const persona = personas.find(p => p.id === row.id);
                            if (persona) startEdit(persona);
                          }}
                          title="Edit Persona"
                          style={{ fontSize: '12px', padding: '6px 12px' }}
                        >
                          Edit
                        </button>
                        <button 
                          className="primary-button"
                          onClick={() => {
                            const persona = personas.find(p => p.id === row.id);
                            if (persona) openAssignModal(persona);
                          }}
                          title="Manage Organization Assignments"
                          style={{ fontSize: '12px', padding: '6px 12px' }}
                        >
                          Assign
                        </button>
                        <button 
                          className="secondary-button"
                          onClick={() => {
                            const persona = personas.find(p => p.id === row.id);
                            if (persona) openVisibilityModal(persona);
                          }}
                          title="Manage Visibility per Organization"
                          style={{ fontSize: '12px', padding: '6px 12px' }}
                        >
                          👁️ Visibility
                        </button>
                        <button 
                          className="delete-button"
                          onClick={() => handleDeletePersona(Number(row.id))}
                          title="Delete Persona"
                          style={{ fontSize: '12px', padding: '6px 12px' }}
                        >
                          Delete
                        </button>
                      </div>
                    )
                  }
                ]}
                rows={personas.map(p => ({
                  id: p.id,
                  name: p.name,
                  description: p.description,
                  role_focus: p.role_focus,
                  temperature: p.temperature,
                  is_public: p.is_public,
                  is_system_default: p.is_system_default,
                  is_active: p.is_active,
                  created_at: p.created_at
                }))}
                height="100%"
                loading={loading}
                className="personas-grid"
              />
            )}
          </div>
        </div>
      )}

      {/* Assign Persona to Organizations Modal */}
      {showAssignModal && (
        <div className="modal-overlay" onClick={closeAssignModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <div>
                <h2>Manage Organizations</h2>
                <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                  Assign "{showAssignModal.name}" to organizations
                </p>
              </div>
            </div>
            <div className="modal-body">
              {loadingOrgs ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Loading organizations...</p>
                </div>
              ) : organizations.length === 0 ? (
                <p>No organizations found.</p>
              ) : (
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                        <th style={{ textAlign: 'left', padding: '12px 8px', fontSize: '14px' }}>Organization</th>
                        <th style={{ textAlign: 'center', padding: '12px 8px', fontSize: '14px' }}>Status</th>
                        <th style={{ textAlign: 'right', padding: '12px 8px', fontSize: '14px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {organizations.map((org) => (
                        <tr key={org.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                          <td style={{ padding: '12px 8px' }}>
                            <div>
                              <strong>{org.name}</strong>
                              {org.subdomain && (
                                <>
                                  <br />
                                  <small style={{ color: '#888' }}>{org.subdomain}</small>
                                </>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                            {org.has_persona ? (
                              org.is_default ? (
                                <span style={{ 
                                  background: '#10b981', 
                                  color: 'white', 
                                  padding: '4px 8px', 
                                  borderRadius: '12px', 
                                  fontSize: '12px' 
                                }}>
                                  ⭐ Default
                                </span>
                              ) : (
                                <span style={{ 
                                  background: '#667eea', 
                                  color: 'white', 
                                  padding: '4px 8px', 
                                  borderRadius: '12px', 
                                  fontSize: '12px' 
                                }}>
                                  Assigned
                                </span>
                              )
                            ) : (
                              <span style={{ color: '#666', fontSize: '12px' }}>Not assigned</span>
                            )}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                            <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                              {org.has_persona ? (
                                <>
                                  {!org.is_default && (
                                    <button 
                                      className="secondary-button"
                                      onClick={() => handleAssignPersona(org.id, showAssignModal.id, true)}
                                      style={{ fontSize: '11px', padding: '4px 8px' }}
                                    >
                                      Set Default
                                    </button>
                                  )}
                                  <button 
                                    className="delete-button"
                                    onClick={() => handleUnassignPersona(org.id, showAssignModal.id)}
                                    style={{ fontSize: '11px', padding: '4px 8px' }}
                                  >
                                    Unassign
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button 
                                    className="secondary-button"
                                    onClick={() => handleAssignPersona(org.id, showAssignModal.id, false)}
                                    style={{ fontSize: '11px', padding: '4px 8px' }}
                                  >
                                    Assign
                                  </button>
                                  <button 
                                    className="primary-button"
                                    onClick={() => handleAssignPersona(org.id, showAssignModal.id, true)}
                                    style={{ fontSize: '11px', padding: '4px 8px' }}
                                  >
                                    Assign as Default
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button 
                type="button"
                className="secondary-button"
                onClick={closeAssignModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Persona Visibility Modal */}
      {showVisibilityModal && (
        <div className="modal-overlay" onClick={closeVisibilityModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', width: '90%' }}>
            <div className="modal-header">
              <div>
                <h2>👁️ Visibility Settings</h2>
                <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                  Control visibility of "{showVisibilityModal.name}" per organization
                </p>
              </div>
            </div>
            <div className="modal-body">
              {/* Top row: Stats + Quick Actions + Filter */}
              <div style={{ 
                display: 'flex', 
                gap: '16px', 
                marginBottom: '16px',
                flexWrap: 'wrap',
                alignItems: 'center'
              }}>
                {/* Stats */}
                <div style={{ 
                  display: 'flex', 
                  gap: '16px', 
                  padding: '8px 16px', 
                  background: 'var(--surface-color)', 
                  borderRadius: '8px', 
                  border: '1px solid var(--border-color)'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#4caf50' }}>
                      {visibilityOrgs.filter(o => o.visibility === 'active' || !o.visibility).length}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>Active</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#ff9800' }}>
                      {visibilityOrgs.filter(o => o.visibility === 'inactive').length}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>Inactive</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#f44336' }}>
                      {visibilityOrgs.filter(o => o.visibility === 'hidden').length}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>Hidden</div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button
                    className="secondary-button"
                    onClick={() => handleBulkVisibility(showVisibilityModal.id, 'active')}
                    style={{ fontSize: '11px', padding: '6px 10px' }}
                  >
                    ✅ All Active
                  </button>
                  <button
                    className="secondary-button"
                    onClick={() => handleBulkVisibility(showVisibilityModal.id, 'inactive')}
                    style={{ fontSize: '11px', padding: '6px 10px' }}
                  >
                    ⏸️ All Inactive
                  </button>
                  <button
                    className="secondary-button"
                    onClick={() => handleBulkVisibility(showVisibilityModal.id, 'hidden')}
                    style={{ fontSize: '11px', padding: '6px 10px' }}
                  >
                    🚫 All Hidden
                  </button>
                </div>

                {/* Filter */}
                <div style={{ marginLeft: 'auto' }}>
                  <select 
                    value={visibilityFilter} 
                    onChange={(e) => setVisibilityFilter(e.target.value as any)}
                    style={{ 
                      padding: '6px 12px', 
                      borderRadius: '6px',
                      background: 'var(--surface-color)',
                      color: 'var(--text-primary)',
                      border: '1px solid var(--border-color)'
                    }}
                  >
                    <option value="all">All ({visibilityOrgs.length})</option>
                    <option value="active">Active ({visibilityOrgs.filter(o => o.visibility === 'active' || !o.visibility).length})</option>
                    <option value="inactive">Inactive ({visibilityOrgs.filter(o => o.visibility === 'inactive').length})</option>
                    <option value="hidden">Hidden ({visibilityOrgs.filter(o => o.visibility === 'hidden').length})</option>
                  </select>
                </div>
              </div>

              {loadingVisibility ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Loading visibility settings...</p>
                </div>
              ) : (
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <th style={{ textAlign: 'left', padding: '12px 8px', fontSize: '14px' }}>Organization</th>
                        <th style={{ textAlign: 'center', padding: '12px 8px', fontSize: '14px' }}>Visibility</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredVisibilityOrgs.map((org) => (
                        <tr 
                          key={org.id} 
                          style={{ 
                            borderBottom: '1px solid var(--border-color)',
                            opacity: org.visibility === 'hidden' ? 0.5 : 1
                          }}
                        >
                          <td style={{ padding: '12px 8px' }}>
                            <div>
                              <strong>{org.name}</strong>
                              {org.subdomain && (
                                <>
                                  <br />
                                  <small style={{ color: 'var(--text-secondary)' }}>{org.subdomain}</small>
                                </>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                              <button
                                onClick={() => handleSetVisibility(showVisibilityModal.id, org.id, 'active')}
                                style={{
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  background: (org.visibility === 'active' || !org.visibility) 
                                    ? '#4caf50' 
                                    : 'var(--surface-color)',
                                  color: (org.visibility === 'active' || !org.visibility) 
                                    ? 'white' 
                                    : 'var(--text-secondary)',
                                  borderWidth: '1px',
                                  borderStyle: 'solid',
                                  borderColor: (org.visibility === 'active' || !org.visibility) 
                                    ? '#4caf50' 
                                    : 'var(--border-color)'
                                }}
                                title="Visible and selectable"
                              >
                                ✅ Active
                              </button>
                              <button
                                onClick={() => handleSetVisibility(showVisibilityModal.id, org.id, 'inactive')}
                                style={{
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  background: org.visibility === 'inactive' 
                                    ? '#ff9800' 
                                    : 'var(--surface-color)',
                                  color: org.visibility === 'inactive' 
                                    ? 'white' 
                                    : 'var(--text-secondary)',
                                  borderWidth: '1px',
                                  borderStyle: 'solid',
                                  borderColor: org.visibility === 'inactive' 
                                    ? '#ff9800' 
                                    : 'var(--border-color)'
                                }}
                                title="Visible but greyed out (not selectable)"
                              >
                                ⏸️ Inactive
                              </button>
                              <button
                                onClick={() => handleSetVisibility(showVisibilityModal.id, org.id, 'hidden')}
                                style={{
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  background: org.visibility === 'hidden' 
                                    ? '#f44336' 
                                    : 'var(--surface-color)',
                                  color: org.visibility === 'hidden' 
                                    ? 'white' 
                                    : 'var(--text-secondary)',
                                  borderWidth: '1px',
                                  borderStyle: 'solid',
                                  borderColor: org.visibility === 'hidden' 
                                    ? '#f44336' 
                                    : 'var(--border-color)'
                                }}
                                title="Completely hidden from this organization"
                              >
                                🚫 Hidden
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button 
                type="button"
                className="secondary-button"
                onClick={closeVisibilityModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Personas by Organization Modal */}
      {showOrgPersonasModal && (
        <div className="modal-overlay" onClick={closeOrgPersonasModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <div>
                <h2>Manage Personas</h2>
                <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                  Assign personas to "{showOrgPersonasModal.name}"
                </p>
              </div>
            </div>
            <div className="modal-body">
              {loadingOrgPersonas ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Loading personas...</p>
                </div>
              ) : orgPersonas.length === 0 ? (
                <p>No personas available.</p>
              ) : (
                <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                        <th style={{ textAlign: 'left', padding: '12px 8px', fontSize: '14px' }}>Persona</th>
                        <th style={{ textAlign: 'center', padding: '12px 8px', fontSize: '14px' }}>Role</th>
                        <th style={{ textAlign: 'center', padding: '12px 8px', fontSize: '14px' }}>Status</th>
                        <th style={{ textAlign: 'right', padding: '12px 8px', fontSize: '14px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orgPersonas.map((persona) => (
                        <tr key={persona.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                          <td style={{ padding: '12px 8px' }}>
                            <div>
                              <strong>{persona.name}</strong>
                              {persona.description && (
                                <>
                                  <br />
                                  <small style={{ color: '#888' }}>{persona.description}</small>
                                </>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                            <span style={{ 
                              background: '#667eea', 
                              color: 'white', 
                              padding: '4px 8px', 
                              borderRadius: '12px', 
                              fontSize: '12px' 
                            }}>
                              {persona.role_focus || 'general'}
                            </span>
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                            {persona.is_assigned ? (
                              persona.is_default ? (
                                <span style={{ 
                                  background: '#10b981', 
                                  color: 'white', 
                                  padding: '4px 8px', 
                                  borderRadius: '12px', 
                                  fontSize: '12px' 
                                }}>
                                  ⭐ Default
                                </span>
                              ) : (
                                <span style={{ 
                                  background: '#667eea', 
                                  color: 'white', 
                                  padding: '4px 8px', 
                                  borderRadius: '12px', 
                                  fontSize: '12px' 
                                }}>
                                  Assigned
                                </span>
                              )
                            ) : (
                              <span style={{ color: '#666', fontSize: '12px' }}>Not assigned</span>
                            )}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                            <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                              {persona.is_assigned ? (
                                <>
                                  {!persona.is_default && (
                                    <button 
                                      className="secondary-button"
                                      onClick={() => handleAssignPersonaToOrg(showOrgPersonasModal.id, persona.id, true)}
                                      style={{ fontSize: '11px', padding: '4px 8px' }}
                                    >
                                      Set Default
                                    </button>
                                  )}
                                  <button 
                                    className="delete-button"
                                    onClick={() => handleUnassignPersonaFromOrg(showOrgPersonasModal.id, persona.id)}
                                    style={{ fontSize: '11px', padding: '4px 8px' }}
                                  >
                                    Unassign
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button 
                                    className="secondary-button"
                                    onClick={() => handleAssignPersonaToOrg(showOrgPersonasModal.id, persona.id, false)}
                                    style={{ fontSize: '11px', padding: '4px 8px' }}
                                  >
                                    Assign
                                  </button>
                                  <button 
                                    className="primary-button"
                                    onClick={() => handleAssignPersonaToOrg(showOrgPersonasModal.id, persona.id, true)}
                                    style={{ fontSize: '11px', padding: '4px 8px' }}
                                  >
                                    Assign as Default
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button 
                type="button"
                className="secondary-button"
                onClick={closeOrgPersonasModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PersonasPanel;

